/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { AllCommunityModule } from 'ag-grid-community'
import type { ColDef } from 'ag-grid-community'
import { AgGridProvider, AgGridReact } from 'ag-grid-react'
import colors from '@sportninja/common/constants/appColors'
import { font, weightSemiBold } from 'src/components/css'
import { dataGridTheme } from './theme'
import { DataGridLoadingOverlay, DataGridNoRowsOverlay } from './overlays'
import type { DataGridProps } from './types'

/**
 * The Community feature set (MIT). Range selection, the fill handle and the rich
 * select editor are Enterprise and deliberately absent — nothing here needs a
 * licence key.
 */
const COMMUNITY_MODULES = [AllCommunityModule]

/**
 * Column behaviour every grid in the app gets for free. A caller's own
 * `defaultColDef` is shallow-merged OVER this, so opting a grid out of sorting
 * or resizing stays a one-line override rather than a re-declaration.
 *
 * `unSortIcon` is what makes a sortable column LOOK sortable. AG Grid shows an
 * arrow only once you have already sorted; every antd table in the app carries
 * the double caret from the start, and a column that gives no hint reads as
 * static. Columns that opt out of sorting still show nothing.
 */
const BASE_DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  resizable: true,
  unSortIcon: true,
}

/**
 * `${font.body}` is what makes `fontFamily: 'inherit'` in the theme resolve to
 * the tenant font. `min-width: 0` is the load-bearing half of the containment
 * pair: without it a grid inside a flex column refuses to shrink below its
 * content width and pushes the PAGE sideways instead of scrolling inside itself.
 */
const wrapperStyle = css`
  ${font.body}
  width: 100%;
  min-width: 0;

  /*
   * Column headers match the app's antd tables (Statistics, Standings): body
   * size, sentence case, semi-bold, full-strength white. They used to be 11px
   * uppercase and tracked out, borrowed from the registration tables — two
   * header conventions in one product, and a grid that read as a widget
   * dropped into the page rather than one of its tables.
   */
  .ag-header-cell-text {
    ${weightSemiBold}
    font-size: 14px;
    color: ${colors.NEUTRAL_0};
  }

  /*
   * A GROUP header is a different job — it labels a band of columns rather than
   * naming a value — so it keeps the small-caps treatment and steps back in
   * colour. That contrast is what stops the two header rows reading as one.
   */
  .ag-header-group-text {
    font-size: 11px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${colors.NEUTRAL_50};
  }

  /*
   * The sort affordance, coloured like the antd tables': a muted double caret
   * while the column is unsorted, the tenant accent once it is. The theme's
   * accentColor does not reach these — Quartz paints sort icons in the header
   * text colour, which would make an active sort indistinguishable from an
   * inactive one.
   */
  .ag-sort-none-icon {
    color: ${colors.NEUTRAL_100};
  }
  .ag-sort-ascending-icon,
  .ag-sort-descending-icon {
    color: ${colors.PRIMARY};
  }

  .ag-cell {
    font-variant-numeric: tabular-nums;
  }

  /*
   * A grid wider than its container scrolls sideways, and the browser's default
   * overlay scrollbar is a near-invisible hairline against a dark surface — so
   * the columns past the fold read as MISSING rather than as scrolled away.
   * This is the only cue that there is more sheet to the right.
   */
  .ag-body-horizontal-scroll-viewport {
    &::-webkit-scrollbar {
      height: 10px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 5px;
      background: ${colors.SECONDARY_100};
    }
    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.NEUTRAL_400};
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
`

/**
 * Shared AG Grid (Community) wrapper.
 *
 * Owns exactly the concerns that must not diverge between grids — module
 * registration, the tenant-aware dark theme, typography, width containment and
 * localized overlays — and passes everything else straight through. Columns,
 * row types, editability and keyboard behaviour belong to the feature.
 *
 * @example
 * <DataGrid<BalanceGridRow>
 *   rowData={rows}
 *   columnDefs={columns}
 *   getRowId={({ data }) => data.key}
 * />
 */
const DataGrid = <TRow,>({
  defaultColDef,
  loading = false,
  className,
  'data-testid': testId,
  ...gridOptions
}: DataGridProps<TRow>) => {
  'use memo'

  const mergedDefaultColDef: ColDef<TRow> = {
    ...(BASE_DEFAULT_COL_DEF as ColDef<TRow>),
    ...defaultColDef,
  }

  return (
    <div css={wrapperStyle} className={className} data-testid={testId}>
      <AgGridProvider modules={COMMUNITY_MODULES}>
        <AgGridReact<TRow>
          theme={dataGridTheme}
          defaultColDef={mergedDefaultColDef}
          loading={loading}
          loadingOverlayComponent={DataGridLoadingOverlay}
          noRowsOverlayComponent={DataGridNoRowsOverlay}
          {...gridOptions}
        />
      </AgGridProvider>
    </div>
  )
}

export type { DataGridProps }
export default DataGrid
